import React, { useEffect, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import Form from "../commons/components/form/Form";
import PricingField from "../payment/PricingField";
import TextInput from "../commons/components/form/input/TextInput";
import InputState from "../commons/model/form/InputState";
import { required } from "../commons/model/form/Validators";
import StripeError from "../commons/model/pricing/StripeError";
import { BilledInterval } from "../commons/model/pricing/BilledInterval";
import TaxRate from "../commons/model/pricing/TaxRate";
import { FormBackLinkProps } from "../commons/components/form/FormBackLink";
import ConsentCheckboxInput from "../legal/ConsentCheckboxInput";
import Country from "../commons/model/address/Country";
import amexImg from "../../resources/images/cards/amex.svg";
import cupImg from "../../resources/images/cards/cup.svg";
import mastercardImg from "../../resources/images/cards/mastercard.svg";
import visaImg from "../../resources/images/cards/visa.svg";
import jcbImg from "../../resources/images/cards/jcb.svg";
import discoverImg from "../../resources/images/cards/discover.svg";
import "./PaymentForm.scss";
import { LegalConsent } from "../legal/LegalResource";

interface PaymentFormProps {
  stripePlanId: string;
  priceExcludingTaxInCents: number;
  currency: string;
  billedInterval: BilledInterval;
  taxRate?: TaxRate;
  trialPeriodDay: number;
  country: Country;
  validateEndpoint: string;
  submitLabel: string;
  onSubmitSucceeded: (payload: any, form: { [key: string]: InputState }) => void;
  errorMsg?: string;
  stripeError?: StripeError;
  contactUsEmail: string;
  backLink: FormBackLinkProps;
  legalConsent?: LegalConsent;
}

const PaymentForm: React.FC<React.PropsWithChildren<PaymentFormProps>> = (props) => {
  const [errorMsg, setErrorMsg] = useState<string | undefined>(props.errorMsg);
  const [couponId, setCouponId] = useState<string | undefined>();
  const stripe = useStripe(); // Access Stripe
  const elements = useElements(); // Access elements like CardElement

  useEffect(() => {
    if (props.stripeError) {
      handleStripeError(props.stripeError);
    }
  }, [props.stripeError]);

  const handleStripeError = (stripeError?: StripeError) => {
    if (stripeError) {
      const status = stripeError.error["status"];
      const contactUsMessage =
        i18n["Form.Error.StripePaymentFails.ContactUs"] + props.contactUsEmail;

      if (status === "ERROR" && stripeError.error["errorMessage"]) {
        setErrorMsg(stripeError.error["errorMessage"] + "<br>" + contactUsMessage);
        stripeError.reject();
      } else if (status === "REQUIRES_ACTION") {
        const paymentIntentSecret = stripeError.error["paymentIntentSecret"];
        stripe.confirmCardPayment(paymentIntentSecret).then((result) => {
          if (result.error) {
            setErrorMsg(result.error.message + "<br>" + contactUsMessage);
            stripeError.reject();
          }
        });
      }
    }
  };

  const submit = async (form: { [key: string]: InputState }) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded
      return;
    }

    const cardholderName = form["cardholder"].value;
    const cardElement = elements.getElement(CardElement); // Get the card details from CardElement

    const { token, error } = await stripe.createToken(cardElement!, {
      name: cardholderName, // Pass the cardholder's name
    });

    if (error) {
      setErrorMsg(error.message);
      return;
    }

    const payload = {
      payment: {
        stripePlanId: props.stripePlanId,
        stripeToken: token?.id,
        stripeTaxRateId: props.taxRate?.id,
        coupon: couponId,
      },
    };

    // Proceed with the payload and form submission
    props.onSubmitSucceeded(payload, form);
  };

  return (
    <Form
      titleLabel={i18n["Form.DoctorRegistration.Payment.Title"]}
      submitLabel={props.submitLabel}
      submit={submit}
      submitIcon="ic_lock"
      generalErrorMsg={errorMsg}
      backLink={props.backLink}
    >
      <PricingField {...props} setErrorMsg={setErrorMsg} onCouponChanged={setCouponId} />
      <hr className="hr-secondary" />
      <div className="input-group h-flex space-between">
        <span>{i18n["Global.AcceptedCreditCard"]}</span>
        <div>
          <img className="card-img" alt="amex" src={amexImg} />
          <img className="card-img" alt="mastercard" src={mastercardImg} />
          <img className="card-img" alt="visa" src={visaImg} />
          <img className="card-img" alt="jcb" src={jcbImg} />
          <img className="card-img" alt="discover" src={discoverImg} />
          <img className="card-img" alt="cup" src={cupImg} />
        </div>
      </div>
      <TextInput
        name="cardholder"
        label={i18n["Global.Cardholder"]}
        placeholder={i18n["Form.DoctorRegistration.Payment.Placeholder.Cardholder"]}
        validators={[required()]}
      />
      <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
      <div className="input-group powered-by-stripe-img" />
      {props.legalConsent && (
        <ConsentCheckboxInput
          name="legalDocuments"
          legalResources={props.legalConsent.legalResources}
          country={props.country}
          practiceToken={props.legalConsent.practiceToken}
          onError={props.legalConsent.onError}
        />
      )}
    </Form>
  );
};

export default PaymentForm;
